import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { apiFetch } from "../utils/api.js";
import dayjs from "dayjs";
import { SUBSCRIPTION_NAMES } from "../utils/consts.js";
import { classifyError } from "../utils/api-error.js";

export const checkUserData = createAsyncThunk("user/userData", async () => {
    const response = await apiFetch("/v2/user/@me").then((r) => {
        if (r.status >= 400) {
            const err = new Error("Auth Error");
            err.status = r.status;
            throw err;
        }
        return r;
    });
    return response.json();
});

export const fetchPurchases = createAsyncThunk("user/purchases", async () => {
    const response = await apiFetch("/v2/organisation/purchases").then((r) => {
        if (r.status >= 400) {
            const err = new Error("Fetch Error");
            err.status = r.status;
            throw err;
        }
        return r;
    });
    return response.json();
});

export const fetchSubscription = createAsyncThunk("user/fetchSubscription", async () => {
    const response = await apiFetch("/v2/organisation/subscription").then((r) => {
        if (r.status >= 400) {
            const err = new Error("Fetch Error");
            err.status = r.status;
            throw err;
        }
        return r;
    });
    const data = await response.json();
    return data;
});

export const fetchCredits = createAsyncThunk("user/fetchCredits", async () => {
    const response = await apiFetch("/v2/organisation/credits").then((r) => {
        if (r.status >= 400) {
            const err = new Error("Fetch Error");
            err.status = r.status;
            throw err;
        }
        return r;
    });
    return response.json();
});

export const fetchOrgStats = createAsyncThunk("user/fetchOrgStats", async () => {
    const response = await apiFetch("/v2/survey?stats=2").then((r) => {
        if (r.status >= 400) {
            const err = new Error("Fetch Error");
            err.status = r.status;
            throw err;
        }
        return r;
    });
    return response.json();
});

export const updateUserStatus = createAsyncThunk(
    "user/updateStatus",
    async ({ userId, active, inactive_reason }) => {
        const response = await apiFetch(`/v2/user/${userId}`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ active, inactive_reason }),
        }).then((r) => {
            if (r.status >= 400) throw new Error("Update Error");
            return r;
        });
        return response.json();
    }
);

// stripe_item.price_id for the managed plan. The row is configured out-of-band
// (directly in the database, not via deploy SQL), so this literal mirrors it.
const MANAGED_PRICE_ID = "price_MANAGED";

const findManagedPurchase = (purchases) => (
    Array.isArray(purchases) ? purchases.find((p) => p?.item_id === MANAGED_PRICE_ID) : null
);

export const isManagedAccount = (purchases, subscriptionData) => (
    Boolean(findManagedPurchase(purchases)) || subscriptionData?.item_id === MANAGED_PRICE_ID
);

const calculateActiveSubscription = (userData, purchases, subscriptionData) => {
    const isMainAdmin = userData?.is_main_admin;
    if (!isMainAdmin) return false;

    if (isManagedAccount(purchases, subscriptionData)) {
        const managed = findManagedPurchase(purchases);
        if (!managed?.expires) return true;
        return dayjs(managed.expires).isValid() && dayjs(managed.expires).isAfter(dayjs());
    }

    if (!purchases || purchases.length === 0) return false;
    const recurringPurchase = purchases.find((product) => product?.expires);
    const expiryDate = recurringPurchase?.expires;
    if (expiryDate && dayjs(expiryDate).isValid() && dayjs(expiryDate).isAfter(dayjs())) {
        return true;
    }
    return false;
};

const calculateSubscriptionDetails = (userData, purchases, subscriptionData) => {
    const isMainAdmin = userData?.is_main_admin;
    if (!isMainAdmin) return SUBSCRIPTION_NAMES.FREE_INDIVIDUAL;

    if (isManagedAccount(purchases, subscriptionData)) {
        const managed = findManagedPurchase(purchases);
        return managed?.description || "Managed Account";
    }

    if (!purchases || purchases.length === 0) return SUBSCRIPTION_NAMES.FREE_INDIVIDUAL;
    const recurringPurchase = purchases.find((purchase) => purchase?.expires);
    if (recurringPurchase && recurringPurchase.expires && dayjs(recurringPurchase.expires).isValid() && dayjs(recurringPurchase.expires).isAfter(dayjs())) {
        return recurringPurchase?.description || "Unknown Subscription";
    }
    return SUBSCRIPTION_NAMES.FREE_INDIVIDUAL;
};

/**
 * Helper function to check if user has the "Account Holder" role
 * @param {object} userData - The user data object containing roles
 * @returns {boolean} True if user has Account Holder role
 */
const calculateIsAccountHolder = (userData) => {
    return Object.values(userData?.roles || {}).includes("Account Holder");
};

export const userSlice = createSlice({
    name: "user",
    initialState: {
        userData: false,
        userDataError: null,
        purchases: [],
        purchasesError: null,
        activeSubscription: false,
        subscriptionDetails: SUBSCRIPTION_NAMES.FREE_INDIVIDUAL,
        subscriptionData: null,
        subscriptionError: null,
        isAccountHolder: false,
        isManaged: false,
        creditsData: null,
        creditsError: null,
        orgStats: null,
        orgStatsError: null,
    },
    extraReducers(builder) {
        // Check User Async States
        builder.addCase(checkUserData.pending, (state) => {
            state.userData = false;
            state.isAccountHolder = false;
            state.userDataError = null;
        });
        builder.addCase(checkUserData.fulfilled, (state, action) => {
            state.userData = action.payload;
            state.isManaged = isManagedAccount(state.purchases, state.subscriptionData);
            state.activeSubscription = calculateActiveSubscription(action.payload, state.purchases, state.subscriptionData);
            state.subscriptionDetails = calculateSubscriptionDetails(action.payload, state.purchases, state.subscriptionData);
            state.isAccountHolder = calculateIsAccountHolder(action.payload);
            state.userDataError = null;
        });
        builder.addCase(checkUserData.rejected, (state, action) => {
            state.userData = false;
            state.isManaged = false;
            state.activeSubscription = false;
            state.subscriptionDetails = SUBSCRIPTION_NAMES.FREE_INDIVIDUAL;
            state.isAccountHolder = false;
            state.userDataError = classifyError(action.error) ?? null;
        });
        // Fetch Purchases Async States
        builder.addCase(fetchPurchases.pending, (state) => {
            state.purchases = [];
            state.purchasesError = null;
        });
        builder.addCase(fetchPurchases.fulfilled, (state, action) => {
            state.purchases = action.payload;
            state.isManaged = isManagedAccount(action.payload, state.subscriptionData);
            state.activeSubscription = calculateActiveSubscription(state.userData, action.payload, state.subscriptionData);
            state.subscriptionDetails = calculateSubscriptionDetails(state.userData, action.payload, state.subscriptionData);
            state.purchasesError = null;
        });
        builder.addCase(fetchPurchases.rejected, (state, action) => {
            state.purchases = [];
            state.isManaged = isManagedAccount([], state.subscriptionData);
            state.activeSubscription = false;
            state.subscriptionDetails = SUBSCRIPTION_NAMES.FREE_INDIVIDUAL;
            state.purchasesError = classifyError(action.error) ?? null;
        });
        // Fetch Subscription Async States
        builder.addCase(fetchSubscription.pending, (state) => {
            state.subscriptionError = null;
        });
        builder.addCase(fetchSubscription.fulfilled, (state, action) => {
            const subscription = action.payload;
            // The API returns an empty object if there is no subscription.
            if (subscription && Object.keys(subscription).length > 0) {
                state.subscriptionData = subscription;
                state.isManaged = isManagedAccount(state.purchases, subscription);
                state.activeSubscription = calculateActiveSubscription(state.userData, state.purchases, subscription);
                state.subscriptionDetails = calculateSubscriptionDetails(state.userData, state.purchases, subscription);
            } else {
                state.subscriptionData = null;
                state.isManaged = isManagedAccount(state.purchases, null);
                state.activeSubscription = calculateActiveSubscription(state.userData, state.purchases, null);
                state.subscriptionDetails = calculateSubscriptionDetails(state.userData, state.purchases, null);
            }
            state.subscriptionError = null;
        });
        builder.addCase(fetchSubscription.rejected, (state, action) => {
            state.subscriptionData = null;
            state.isManaged = isManagedAccount(state.purchases, null);
            state.activeSubscription = calculateActiveSubscription(state.userData, state.purchases, null);
            state.subscriptionDetails = calculateSubscriptionDetails(state.userData, state.purchases, null);
            state.subscriptionError = classifyError(action.error) ?? null;
        });
        // Fetch Credits Async States
        builder.addCase(fetchCredits.pending, (state) => {
            state.creditsError = null;
        });
        builder.addCase(fetchCredits.fulfilled, (state, action) => {
            state.creditsData = action.payload;
            state.creditsError = null;
        });
        builder.addCase(fetchCredits.rejected, (state, action) => {
            state.creditsData = null;
            state.creditsError = classifyError(action.error) ?? null;
        });
        // Fetch Org Stats Async States
        builder.addCase(fetchOrgStats.pending, (state) => {
            state.orgStatsError = null;
        });
        builder.addCase(fetchOrgStats.fulfilled, (state, action) => {
            state.orgStats = action.payload;
            state.orgStatsError = null;
        });
        builder.addCase(fetchOrgStats.rejected, (state, action) => {
            state.orgStats = null;
            state.orgStatsError = classifyError(action.error) ?? null;
        });
    },
});

export const userSelector = (state) => state[userSlice.name];

export default userSlice.reducer;
